// GoogleAuthButton Component
// UPDATED: Uses Google Identity Services (GIS) SDK with signInWithIdToken
// Shows "finncal.com" in consent screen instead of Supabase URL
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { signInWithGoogleIdToken, signInWithGoogle } from '../../services/authService';
import { trackLoginStarted, trackLoginCompleted } from '../../utils/analytics';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleAuthButton = ({ onSuccess, onError, label, disabled }) => {
  const buttonRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  // Refs to hold latest callback values (avoids stale closures in GIS callback)
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  // Handle the credential response from Google
  const handleCredentialResponse = useCallback(async (response) => {
    if (!response.credential) {
      onErrorRef.current?.('No credential received from Google');
      return;
    }

    setLoading(true);
    trackLoginStarted();

    try {
      const result = await signInWithGoogleIdToken(response.credential);
      if (result.success) {
        trackLoginCompleted(result.user?.id);
        onSuccessRef.current?.();
      } else {
        onErrorRef.current?.(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      onErrorRef.current?.(error.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the Google Identity Services script
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('REACT_APP_GOOGLE_CLIENT_ID not configured — using OAuth redirect fallback');
      setUseFallback(true);
      return;
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      setScriptLoaded(true);
      return;
    }

    // Check if script tag already exists (avoid duplicates)
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      // Script tag exists but hasn't loaded yet — wait for it
      const checkLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          setScriptLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
      setScriptError(true);
      setUseFallback(true);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize Google Identity Services and render button
  useEffect(() => {
    if (!scriptLoaded || !window.google?.accounts?.id || disabled || !buttonRef.current) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        // NOTE: use_fedcm_for_prompt intentionally omitted — FedCM silently fails on
        // Firefox, Safari, Chrome incognito, and users not signed into Chrome.
        // Without it, GIS uses the standard popup flow which works on all browsers.
      });

      // Clear previous button content before re-rendering
      buttonRef.current.replaceChildren();

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: label?.toLowerCase().includes('sign up') ? 'signup_with' : 'signin_with',
        width: buttonRef.current.parentElement?.offsetWidth || 320,
        logo_alignment: 'left',
      });
    } catch (error) {
      console.error('Error initializing Google Identity Services:', error);
      setUseFallback(true);
    }
  }, [scriptLoaded, disabled, label, handleCredentialResponse]);

  // Fallback: use OAuth redirect flow when GIS fails to load or render
  const handleFallbackClick = async () => {
    setLoading(true);
    trackLoginStarted();
    try {
      await signInWithGoogle({ returnPath: window.location.pathname });
      // signInWithGoogle redirects the page — onSuccess won't be called here
    } catch (error) {
      onErrorRef.current?.(error.message || 'Google sign-in failed');
      setLoading(false);
    }
  };

  // When disabled (signup without consent), show a grayed-out placeholder
  if (disabled) {
    return (
      <button
        className="google-auth-button"
        disabled
        style={{ opacity: 0.5, cursor: 'not-allowed' }}
      >
        <svg className="google-icon" width="20" height="20" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {label || 'Continue with Google'}
      </button>
    );
  }

  const googleIconSvg = (
    <svg className="google-icon" width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  // Show fallback button when GIS script fails to load or renders an empty container
  if (useFallback || scriptError) {
    return (
      <button
        className="google-auth-button"
        onClick={handleFallbackClick}
        disabled={loading}
        style={loading ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
      >
        {googleIconSvg}
        {loading ? 'Connecting...' : label || 'Continue with Google'}
      </button>
    );
  }

  // Show loading state while script loads or auth is in progress
  if (!scriptLoaded || loading) {
    return (
      <button className="google-auth-button" disabled>
        {googleIconSvg}
        {loading ? 'Connecting...' : 'Loading...'}
      </button>
    );
  }

  // Render Google's official button via GIS SDK
  return (
    <div
      ref={buttonRef}
      className="google-gis-button-container"
      style={{ display: 'flex', justifyContent: 'center', minHeight: '44px' }}
    />
  );
};

export default GoogleAuthButton;
